import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useDocBar } from "../shell/context";

/**
 * One document, full width: the paper on the left and what the reader can do
 * about it on the right.
 *
 * An invoice, a quote and a contract used to open in a 672-pixel dialog, which
 * put a four-column table and a signature pad inside a letter box - and on a
 * phone inside a letter box inside a letter box. A page gives the document the
 * width it was written for and keeps its one action in sight.
 *
 * The side column has two slots because a phone needs them in two places:
 *
 * - `lead` is the answer most readers came for - the amount and the deadline.
 *   On a wide screen it heads the side column; on a phone it sits ABOVE the
 *   paper.
 * - `rail` is everything else that belongs beside the document - the payments
 *   received, a contract's facts, a signing form. On a phone it comes AFTER
 *   the paper, so the document is not pushed down the page by its footnotes,
 *   and a contract is read before it is signed.
 *
 * From `lg` up the whole side column stays put while the paper scrolls. Under
 * `lg` the column dissolves (`display: contents`) and its two slots take their
 * places around the paper.
 *
 * `phoneBar` is the document's main action, pinned to the bottom edge of a
 * phone where a thumb already is. The shell's tab bar steps aside for it (see
 * `shell/context.ts`).
 */
export function DocLayout({
  paper,
  lead,
  rail,
  phoneBar,
}: {
  paper: ReactNode;
  lead?: ReactNode;
  rail?: ReactNode;
  phoneBar?: ReactNode;
}) {
  useDocBar(Boolean(phoneBar));

  return (
    <>
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-6">
        <div className="min-w-0">{paper}</div>
        <aside className="min-w-0 max-lg:contents lg:sticky lg:top-24 lg:space-y-4">
          {lead && <div className="min-w-0 max-lg:order-first">{lead}</div>}
          {rail && <div className="min-w-0 space-y-4">{rail}</div>}
        </aside>
      </div>
      {phoneBar &&
        // Through a portal, onto the body. The page this sits in slides in with
        // a transform, and a `fixed` box inside a transformed parent is fixed to
        // that parent rather than to the screen - for the length of the slide
        // the bar would hang off the bottom of the page instead of the window.
        createPortal(
          <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-floating backdrop-blur lg:hidden print:hidden">
            <div className="mx-auto flex max-w-6xl items-center gap-2">{phoneBar}</div>
          </div>,
          document.body,
        )}
    </>
  );
}
